import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import type { ServiceOption } from '../../../services/servicesStore';

interface ServiceFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  categories: string[];
}

const ServiceForm = ({ initialData, onSubmit, onCancel, categories }: ServiceFormProps) => {
  const [formData, setFormData] = useState<any>(() => ({
    id: initialData?.id || crypto.randomUUID(),
    name: initialData?.name || '',
    description: initialData?.description || '',
    longDescription: initialData?.longDescription || '',
    price: initialData?.price || 0,
    pricingMode: initialData?.pricingMode || 'fixed',
    pricingNote: initialData?.pricingNote || '',
    category: initialData?.category || '',
    billingType: initialData?.billingType || 'one-time',
    workDays: initialData?.workDays,
    features: initialData?.features || [],
    timeline: initialData?.timeline || '',
    technicalDetails: initialData?.technicalDetails || {
      technologies: []
    },
    benefits: initialData?.benefits || [],
    popular: initialData?.popular || false,
    image: initialData?.image || '',
    options: initialData?.options || []
  }));

  const [newFeature, setNewFeature] = useState('');
  const [newTechnology, setNewTechnology] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newOption, setNewOption] = useState<ServiceOption>({
    label: '',
    price: 0,
    billingType: 'one-time',
    description: '',
  });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAISettings, setShowAISettings] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(
    localStorage.getItem('ai_generation_enabled') !== 'false'
  );
  const [webhookUrl, setWebhookUrl] = useState(
    localStorage.getItem('ai_webhook_url') || ''
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    maxSize: 5242880,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setFormData({
              ...formData,
              image: e.target.result as string
            });
          }
        };
        reader.readAsDataURL(file);
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_: any, i: number) => i !== index)
    });
  };

  const addTechnology = () => {
    if (newTechnology.trim()) {
      setFormData({
        ...formData,
        technicalDetails: {
          ...formData.technicalDetails,
          technologies: [...formData.technicalDetails.technologies, newTechnology.trim()]
        }
      });
      setNewTechnology('');
    }
  };

  const removeTechnology = (index: number) => {
    setFormData({
      ...formData,
      technicalDetails: {
        ...formData.technicalDetails,
        technologies: formData.technicalDetails.technologies.filter((_: any, i: number) => i !== index)
      }
    });
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, newBenefit.trim()]
      });
      setNewBenefit('');
    }
  };

  const removeBenefit = (index: number) => {
    setFormData({
      ...formData,
      benefits: formData.benefits.filter((_: any, i: number) => i !== index)
    });
  };

  const addOption = () => {
    if (!newOption.label.trim()) {
      return;
    }

    setFormData({
      ...formData,
      options: [
        ...(formData.options || []),
        {
          label: newOption.label.trim(),
          price: Number(newOption.price || 0),
          billingType: newOption.billingType || 'one-time',
          description: newOption.description?.trim() || '',
        },
      ],
    });
    setNewOption({
      label: '',
      price: 0,
      billingType: 'one-time',
      description: '',
    });
  };

  const removeOption = (index: number) => {
    setFormData({
      ...formData,
      options: (formData.options || []).filter((_: ServiceOption, optionIndex: number) => optionIndex !== index),
    });
  };

  const normalizeJsonString = (str: string): string => {
    try {
      // Handle potential HTML entities
      const decoded = str.replace(/&quot;/g, '"').replace(/&#34;/g, '"').replace(/&#39;/g, "'");
      
      // Replace French quotes with standard quotes
      const standardQuotes = decoded
        .replace(/[«»]/g, '"')
        .replace(/[''‹›]/g, "'");
      
      // Ensure property names are properly quoted
      const propertyQuoted = standardQuotes.replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":');
      
      // Ensure string values are properly quoted
      const valueQuoted = propertyQuoted.replace(/:\s*([^",{\[\]}\s][^,{\[\]}\s]*)\s*([,}])/g, ':"$1"$2');
      
      // Clean up any remaining issues
      return valueQuoted
        .replace(/^\uFEFF/, '') // Remove BOM
        .replace(/[\u201C\u201D]/g, '"') // Replace curly quotes
        .replace(/[\u2018\u2019]/g, "'") // Replace curly single quotes
        .replace(/\\"/g, '"') // Replace escaped quotes
        .replace(/\\'/g, "'") // Replace escaped single quotes
        .replace(/\n/g, ' ') // Replace newlines
        .replace(/\r/g, '') // Remove carriage returns
        .replace(/\t/g, ' ') // Replace tabs
        .replace(/\s+/g, ' ') // Replace multiple spaces
        .trim();
    } catch (error) {
      console.error('Error in normalizeJsonString:', error);
      return str;
    }
  };

  const parseResponseData = (responseData: any) => {
    console.log("Raw response data:", responseData);
    
    try {
      // If responseData is a string, try to parse it as JSON
      if (typeof responseData === 'string') {
        try {
          // First, try to parse the normalized string
          const normalizedData = normalizeJsonString(responseData);
          console.log("Normalized data:", normalizedData);
          
          // Check if the normalized data starts with a curly brace
          if (!normalizedData.trim().startsWith('{')) {
            throw new Error('Response is not in JSON format');
          }
          
          responseData = JSON.parse(normalizedData);
        } catch (e) {
          console.error('Failed to parse response as JSON:', e);
          // If parsing fails, try to extract data using regex
          return parseTextResponse(responseData);
        }
      }
      
      // If responseData is an array, take the first element
      const data = Array.isArray(responseData) ? responseData[0] : responseData;
      
      // If data has a body property that's a string, try to parse it
      if (data?.body && typeof data.body === 'string') {
        try {
          const normalizedBody = normalizeJsonString(data.body);
          
          // Check if the normalized body starts with a curly brace
          if (!normalizedBody.trim().startsWith('{')) {
            throw new Error('Response body is not in JSON format');
          }
          
          const parsedBody = JSON.parse(normalizedBody);
          return formatParsedData(parsedBody);
        } catch (e) {
          console.error('Failed to parse body as JSON:', e);
          return parseTextResponse(data.body);
        }
      }
      
      // If data is already an object with the expected structure
      if (typeof data === 'object' && data !== null) {
        return formatParsedData(data);
      }
      
      throw new Error('Unable to parse response data');
    } catch (error) {
      console.error('Error parsing response data:', error);
      throw new Error('Failed to process the AI response. Please try again.');
    }
  };

  const parseTextResponse = (text: string) => {
    console.log("Parsing text response:", text);
    
    const extractValue = (pattern: RegExp) => {
      try {
        const match = text.match(pattern);
        if (!match) return '';
        
        // Clean the extracted value
        return normalizeJsonString(match[1]);
      } catch (e) {
        console.error(`Error extracting value with pattern ${pattern}:`, e);
        return '';
      }
    };

    const extractArray = (text: string, key: string) => {
      try {
        // First try to match JSON array format
        const jsonPattern = new RegExp(`"${key}"\\s*:?\\s*(\\[.*?\\])`, 's');
        const jsonMatch = text.match(jsonPattern);
        
        if (jsonMatch) {
          try {
            const normalizedArray = normalizeJsonString(jsonMatch[1]);
            return JSON.parse(normalizedArray);
          } catch (e) {
            console.error('Failed to parse JSON array:', e);
          }
        }

        // Fallback to comma-separated format
        const pattern = new RegExp(`${key}\\s*:?\\s*\\[([^\\]]+)\\]`);
        const match = text.match(pattern);
        if (match) {
          return match[1]
            .split(',')
            .map(item => normalizeJsonString(item))
            .filter(Boolean);
        }
        return [];
      } catch (e) {
        console.error(`Error extracting array for key ${key}:`, e);
        return [];
      }
    };

    try {
      return {
        name: extractValue(/name\s*:?\s*["']([^"']+)["']/) || serviceName,
        category: extractValue(/category\s*:?\s*["']([^"']+)["']/),
        description: extractValue(/short_description\s*:?\s*["']([^"']+)["']/) || 
                    extractValue(/description\s*:?\s*["']([^"']+)["']/),
        longDescription: extractValue(/long_description\s*:?\s*["']([^"']+)["']/) || 
                        extractValue(/longDescription\s*:?\s*["']([^"']+)["']/),
        price: parseInt(extractValue(/price\s*:?\s*(\d+)/)) || 0,
        billingType: extractValue(/billing_type\s*:?\s*["']([^"']+)["']/) || 'one-time',
        timeline: extractValue(/delay\s*:?\s*["']([^"']+)["']/) || 
                 extractValue(/timeline\s*:?\s*["']([^"']+)["']/),
        features: extractArray(text, 'features'),
        technicalDetails: {
          technologies: extractArray(text, 'technologies')
        },
        benefits: extractArray(text, 'benefits'),
        image: extractValue(/image_url\s*:?\s*["']([^"']+)["']/) || formData.image
      };
    } catch (e) {
      console.error('Error in parseTextResponse:', e);
      throw new Error('Failed to parse the text response. Please try again.');
    }
  };

  const formatParsedData = (data: any) => {
    try {
      // Convert arrays that might be strings
      const formatArray = (value: any) => {
        if (typeof value === 'string') {
          try {
            // Try to parse as JSON first
            const normalizedValue = normalizeJsonString(value);
            return JSON.parse(normalizedValue);
          } catch {
            // If parsing fails, split by comma
            return value.split(',').map(item => normalizeJsonString(item)).filter(Boolean);
          }
        }
        return Array.isArray(value) ? value : [];
      };

      // Convert billing type to match expected values
      const formatBillingType = (billing: string) => {
        const billing_lower = (billing || '').toLowerCase();
        if (billing_lower.includes('mensuel')) return 'monthly';
        if (billing_lower.includes('jour')) return 'days';
        return 'one-time';
      };

      return {
        name: data.name || serviceName,
        category: data.category || formData.category,
        description: data.short_description || data.description || '',
        longDescription: data.long_description || data.longDescription || '',
        price: Number(data.price) || 0,
        pricingMode: data.pricingMode || 'fixed',
        pricingNote: data.pricingNote || '',
        billingType: formatBillingType(data.billing_type || data.billingType),
        workDays: Number(data.workDays) || undefined,
        timeline: data.delay || data.timeline || '',
        features: formatArray(data.features),
        technicalDetails: {
          technologies: formatArray(data.technologies || (data.technicalDetails?.technologies))
        },
        benefits: formatArray(data.benefits),
        image: data.image_url || data.image || formData.image,
        options: Array.isArray(data.options) ? data.options : []
      };
    } catch (e) {
      console.error('Error in formatParsedData:', e);
      throw new Error('Failed to format the parsed data. Please try again.');
    }
  };

  const handleGenerateService = async () => {
    if (!serviceName.trim()) {
      toast.error('Veuillez saisir un nom de service');
      return;
    }

    setIsGenerating(true);

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          service: serviceName
        })
      });

      // Log the response status and headers for debugging
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`HTTP error ${response.status}: ${errorText || 'No error details available'}`);
      }

      // Read the response body as text first
      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      let responseData;
      try {
        // Try to parse as JSON if the response looks like JSON
        if (responseText.trim().startsWith('{') || responseText.trim().startsWith('[')) {
          responseData = JSON.parse(responseText);
        } else {
          // If not JSON, use the text directly
          responseData = responseText;
        }
      } catch (e) {
        console.warn('Response is not valid JSON, treating as text:', e);
        responseData = responseText;
      }

      console.log('Processed response data:', responseData);
      
      const parsedData = parseResponseData(responseData);
      
      setFormData({
        ...formData,
        ...parsedData
      });

      setShowGenerateModal(false);
      toast.success('Service généré avec succès !');
    } catch (error) {
      console.error('Generation error:', error);
      
      let errorMessage = 'Erreur lors de la génération du service.';
      
      if (error instanceof TypeError) {
        errorMessage = 'Erreur de connexion au service. Vérifiez votre connexion internet.';
      } else if (error instanceof SyntaxError) {
        errorMessage = 'Erreur de format dans la réponse du service.';
      } else if (error instanceof Error) {
        if (error.message.includes('HTTP error 500')) {
          errorMessage = 'Erreur interne du service. Veuillez réessayer plus tard ou contacter l\'administrateur.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveAISettings = () => {
    localStorage.setItem('ai_generation_enabled', aiEnabled.toString());
    localStorage.setItem('ai_webhook_url', webhookUrl);
    setShowAISettings(false);
    toast.success('Paramètres IA sauvegardés');
  };

  const handleGenerateClick = () => {
    if (formData.name || formData.description || formData.features.length > 0) {
      if (window.confirm('Cela va écraser les données déjà saisies. Continuer ?')) {
        setShowGenerateModal(true);
      }
    } else {
      setShowGenerateModal(true);
    }
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      if (showAISettings) {
        setShowAISettings(false);
        return;
      }

      if (showGenerateModal) {
        setShowGenerateModal(false);
        return;
      }

      onCancel();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onCancel, showAISettings, showGenerateModal]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b bg-white px-6 py-4">
          <h3 className="text-xl font-semibold">
            {initialData ? 'Modifier le service' : 'Nouveau service'}
          </h3>
          <div className="flex items-center gap-2">
            {aiEnabled && (
              <button
                type="button"
                onClick={handleGenerateClick}
                className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                </svg>
                Générer automatiquement
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowAISettings(true)}
              className="p-2 text-gray-500 transition-colors hover:text-gray-700"
              aria-label="Ouvrir les paramètres IA"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Fermer la fenêtre service"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 overflow-y-auto p-6">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image
            </label>
            <div
              {...getRootProps()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-neon-orange transition-colors"
            >
              <input {...getInputProps()} />
              {formData.image ? (
                <img
                  src={formData.image}
                  alt="Preview"
                  className="max-h-48 mx-auto"
                />
              ) : (
                <p>Glissez une image ou cliquez pour sélectionner</p>
              )}
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({
                  ...formData,
                  name: e.target.value
                })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catégorie
              </label>
              <input
                list="service-categories"
                value={formData.category}
                onChange={(e) => setFormData({
                  ...formData,
                  category: e.target.value
                })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                required
                placeholder="Saisir ou choisir une categorie"
              />
              <datalist id="service-categories">
                {categories.map((category) => (
                  <option key={category} value={category}>
                  </option>
                ))}
              </datalist>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description courte
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({
                ...formData,
                description: e.target.value
              })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description longue
            </label>
            <textarea
              value={formData.longDescription}
              onChange={(e) => setFormData({
                ...formData,
                longDescription: e.target.value
              })}
              rows={4}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prix catalogue de base (EUR)
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({
                  ...formData,
                  price: Number(e.target.value)
                })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                required
              />
              <p className="mt-2 text-xs text-gray-500">
                Ce montant reste la base catalogue en EUR. Le site public et l&apos;admin convertissent ensuite l&apos;affichage selon la devise active.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logique tarifaire
              </label>
              <select
                value={formData.pricingMode}
                onChange={(e) => setFormData({
                  ...formData,
                  pricingMode: e.target.value
                })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                required
              >
                <option value="fixed">Prix fixe</option>
                <option value="starting-at">A partir de</option>
              </select>
              <p className="mt-2 text-xs text-gray-500">
                Utilise &quot;A partir de&quot; pour les offres agence avec options ou périmètre variable.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de facturation
              </label>
              <select
                value={formData.billingType}
                onChange={(e) => setFormData({
                  ...formData,
                  billingType: e.target.value
                })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                required
              >
                <option value="one-time">Paiement unique</option>
                <option value="monthly">Mensuel</option>
                <option value="days">Jours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note tarifaire / sous-titre
              </label>
              <input
                type="text"
                value={formData.pricingNote}
                onChange={(e) => setFormData({
                  ...formData,
                  pricingNote: e.target.value
                })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                placeholder="Base 5 pages, 30 jours de pilotage, sprint 10 jours..."
              />
            </div>
          </div>

          {formData.billingType === 'days' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de jours
              </label>
              <input
                type="number"
                min="1"
                value={formData.workDays || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  workDays: Number(e.target.value) || undefined
                })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                placeholder="Ex: 5"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Délai
            </label>
            <input
              type="text"
              value={formData.timeline}
              onChange={(e) => setFormData({
                ...formData,
                timeline: e.target.value
              })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="popular"
              checked={formData.popular}
              onChange={(e) => setFormData({
                ...formData,
                popular: e.target.checked
              })}
              className="rounded border-gray-300 text-neon-orange focus:ring-neon-orange/20"
            />
            <label htmlFor="popular" className="text-sm font-medium text-gray-700">
              Service populaire
            </label>
          </div>

          {/* Options fréquentes */}
          <div className="border-t pt-6">
            <div className="mb-4">
              <h4 className="text-lg font-semibold">Options fréquentes</h4>
              <p className="mt-1 text-sm text-gray-500">
                Ajoute ici les options vendues en plus de la base: pages supplémentaires,
                langue additionnelle, tracking, créas, connecteurs, etc.
              </p>
            </div>

            <div className="grid gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <input
                  type="text"
                  value={newOption.label}
                  onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                  placeholder="Nom de l'option"
                />
                <input
                  type="number"
                  min="0"
                  value={newOption.price}
                  onChange={(e) => setNewOption({ ...newOption, price: Number(e.target.value) })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                  placeholder="Prix EUR"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                <select
                  value={newOption.billingType}
                  onChange={(e) => setNewOption({ ...newOption, billingType: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                >
                  <option value="one-time">Paiement unique</option>
                  <option value="monthly">Mensuel</option>
                  <option value="days">Par jour</option>
                </select>
                <input
                  type="text"
                  value={newOption.description}
                  onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                  placeholder="Description courte de l'option"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addOption}
                  className="rounded-lg bg-neon-orange px-4 py-2 text-white transition-colors hover:bg-[#FF4500]"
                >
                  Ajouter l&apos;option
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {(formData.options || []).map((option: ServiceOption, index: number) => (
                <div
                  key={`${option.label}-${index}`}
                  className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 md:flex-row md:items-start md:justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="mt-1 text-sm text-gray-500">
                      {option.description || 'Option catalogue'}
                    </div>
                    <div className="mt-2 text-xs uppercase tracking-[0.18em] text-gray-400">
                      {option.billingType === 'monthly'
                        ? 'Mensuel'
                        : option.billingType === 'days'
                          ? 'Par jour'
                          : 'Paiement unique'}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-lg font-bold text-neon-orange">
                      {Number(option.price || 0).toFixed(2)} EUR
                    </div>
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="rounded-lg bg-red-100 px-3 py-2 text-red-700 transition-colors hover:bg-red-200"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fonctionnalités incluses */}
          <div className="border-t pt-6">
            <h4 className="text-lg font-semibold mb-4">Fonctionnalités incluses</h4>
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                  placeholder="Ajouter une fonctionnalité"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                >
                  Ajouter
                </button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.features.map((feature: string, index: number) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <span>{feature}</span>
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Détails techniques */}
          <div className="border-t pt-6">
            <h4 className="text-lg font-semibold mb-4">Détails techniques</h4>
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTechnology}
                  onChange={(e) => setNewTechnology(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                  placeholder="Ajouter une technologie"
                />
                <button
                  type="button"
                  onClick={addTechnology}
                  className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                >
                  Ajouter
                </button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.technicalDetails.technologies.map((tech: string, index: number) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <span>{tech}</span>
                    <button
                      type="button"
                      onClick={() => removeTechnology(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bénéfices */}
          <div className="border-t pt-6">
            <h4 className="text-lg font-semibold mb-4">Bénéfices</h4>
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                  placeholder="Ajouter un bénéfice"
                />
                <button
                  type="button"
                  onClick={addBenefit}
                  className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                >
                  Ajouter
                </button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.benefits.map((benefit: string, index: number) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <span>{benefit}</span>
                    <button
                      type="button"
                      onClick={() => removeBenefit(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
            >
              Sauvegarder
            </button>
          </div>
        </form>

        {/* Generate Service Modal */}
        {showGenerateModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowGenerateModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="text-xl font-semibold">Générer un service</h3>
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  aria-label="Fermer la génération automatique"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
                  disabled={isGenerating}
                >
                  ×
                </button>
              </div>
              <p className="text-gray-600 mb-4">
                Entrez le nom du service que vous souhaitez générer automatiquement.
              </p>
              <div className="space-y-4">
                <input
                  type="text"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                  placeholder="Nom du service"
                  disabled={isGenerating}
                />
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowGenerateModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={isGenerating}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleGenerateService}
                    className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors flex items-center gap-2"
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Génération en cours...
                      </>
                    ) : (
                      'Valider'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* AI Settings Modal */}
        {showAISettings && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowAISettings(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="text-xl font-semibold">Paramètres IA</h3>
                <button
                  type="button"
                  onClick={() => setShowAISettings(false)}
                  aria-label="Fermer les paramètres IA"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Activer la génération automatique par IA
                  </label>
                  <button
                    type="button"
                    onClick={() => setAiEnabled(!aiEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      aiEnabled ? 'bg-neon-orange' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        aiEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL du webhook Make
                  </label>
                  <input
                    type="text"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    placeholder="https://hook.us1.make.com/..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAISettings(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAISettings}
                    className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                  >
                    Sauvegarder
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ServiceForm;
